/* Sprint 2R-4A — Vognmand Onboarding Wizard.
   Multi-step compliance flow that gates entry to the vognmand portal until
   all required onboarding steps + agreement signing are completed.

   Steps:
     1. Company info (CVR, address, bank details, VAT)
     2. Signatory (name, role, authorized)
     3. Insurance (3 mandatory confirmations)
     4. Conflict of interest declaration
     5. Clauses (GDPR, non-bypass, deductions, etc.)
     6. Sign agreement (review + typed name)
*/
import React from "react";
import { useNavigate } from "react-router-dom";
import { scApi } from "../lib/api";
import { Loader2, CheckCircle2, AlertCircle, ChevronRight, FileSignature, ShieldCheck, Building2, UserCheck, Briefcase, Scale, Send } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "company", label: "Firma & Bank", Icon: Building2 },
  { key: "signatory", label: "Underskriver", Icon: UserCheck },
  { key: "insurance", label: "Forsikringer", Icon: ShieldCheck },
  { key: "conflict", label: "Interessekonflikt", Icon: Briefcase },
  { key: "clauses", label: "Klausuler", Icon: Scale },
  { key: "sign", label: "Underskriv", Icon: FileSignature },
];

function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d.map((x) => x.msg).join(", ");
  return e?.message || "Fejl";
}

export default function VognmandOnboardingPage() {
  const nav = useNavigate();
  const [state, setState] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [step, setStep] = React.useState("company");
  const hasToken = !!localStorage.getItem("ema_sc_token");

  const load = React.useCallback(async () => {
    if (!hasToken) return;
    setLoading(true);
    try {
      const r = await scApi().get("/sc/onboarding/state");
      setState(r.data);
      // Auto-advance to first uncompleted step
      const c = r.data.checks || {};
      if (!c.company_info_complete || !c.bank_details_complete) setStep("company");
      else if (!c.signatory_complete) setStep("signatory");
      else if (!c.insurance_confirmed) setStep("insurance");
      else if (!c.conflict_declared) setStep("conflict");
      else if (!c.gdpr_accepted || !c.non_bypass_accepted || !c.deductions_accepted) setStep("clauses");
      else if (!c.agreement_signed) setStep("sign");
      else setStep("sign");
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("ema_sc_token");
        nav("/vognmand/login");
        return;
      }
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [nav, hasToken]);

  React.useEffect(() => {
    if (!hasToken) {
      nav("/vognmand/login");
      return;
    }
    load();
  }, [load, hasToken, nav]);

  if (!hasToken || loading || !state) {
    return (
      <div className="min-h-screen bg-brand-bg2 flex items-center justify-center">
        <Loader2 className="animate-spin text-brand-red" size={32} />
      </div>
    );
  }

  // If approved already, redirect to portal
  if (state.onboarding_status === "approved") {
    nav("/vognmand");
    return null;
  }

  const checks = state.checks || {};

  return (
    <div className="min-h-screen bg-brand-bg2" data-testid="vognmand-onboarding-page">
      <div className="bg-brand-dark text-white">
        <div className="max-w-3xl mx-auto px-4 py-5">
          <div className="kicker text-white/60">Onboarding · Sprint 2R-4A</div>
          <h1 className="font-display font-bold text-2xl mt-1">Vognmandsaftale / Underleverandøraftale</h1>
          <p className="text-sm text-white/70 mt-2 max-w-2xl">
            For at modtage ruter fra EMA Kurer skal du gennemføre denne onboarding og underskrive vores Underleverandøraftale.
            Alle felter behandles fortroligt og bankdetaljer er beskyttet mod adgang fra chauffører.
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="max-w-3xl mx-auto px-4 py-4 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {STEPS.map((s, i) => {
            const done = isStepDone(s.key, checks);
            const active = s.key === step;
            return (
              <button
                key={s.key}
                onClick={() => setStep(s.key)}
                data-testid={`onboarding-step-${s.key}`}
                className={`flex-shrink-0 px-3 py-2 text-xs font-bold inline-flex items-center gap-2 border ${
                  active ? "border-brand-red text-brand-red bg-white"
                    : done ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                    : "border-brand text-brand-text2 bg-white"
                }`}
              >
                <s.Icon size={14} />
                {i + 1}. {s.label}
                {done && <CheckCircle2 size={12} className="text-emerald-600" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pb-12">
        {state.onboarding_status === "submitted" && checks.agreement_signed && (
          <div className="bg-blue-50 border border-blue-200 p-4 mb-4 text-sm text-blue-900" data-testid="onboarding-submitted-banner">
            ✅ Tak — din onboarding er indsendt og afventer EMA Kurer godkendelse. Du modtager besked når den er godkendt.
          </div>
        )}
        {state.onboarding_status === "rejected" && (
          <div className="bg-red-50 border border-red-200 p-4 mb-4 text-sm text-red-900" data-testid="onboarding-rejected-banner">
            ❌ Din onboarding er afvist. <b>Årsag:</b> {state.rejection_reason}
          </div>
        )}
        {state.onboarding_status === "needs_more_info" && (
          <div className="bg-amber-50 border border-amber-200 p-4 mb-4 text-sm text-amber-900" data-testid="onboarding-needs-info-banner">
            ℹ️ EMA Kurer beder om flere oplysninger: {state.admin_note}
          </div>
        )}
        {state.checks && !state.checks.at_least_one_vehicle_approved && !state.vehicle_requirement_exempt && (
          <div className="bg-amber-50 border border-amber-200 p-4 mb-4 text-sm text-amber-900" data-testid="wizard-vehicle-missing">
            ⚠ <b>Du mangler at få godkendt mindst ét køretøj, før du kan modtage ruter.</b><br/>
            Tilføj køretøj via portalen (fanen Køretøjer) og afvent EMA-godkendelse. Du kan stadig fuldføre aftale-underskrift nu.
          </div>
        )}
        {state.vehicle_requirement_exempt && (
          <div className="bg-blue-50 border border-blue-200 p-4 mb-4 text-sm text-blue-900" data-testid="wizard-vehicle-exempt">
            ℹ EMA Kurer har fritaget jer fra køretøjs-krav: {state.vehicle_exemption_reason}
          </div>
        )}

        {step === "company" && <CompanyStep state={state} onSaved={load} />}
        {step === "signatory" && <SignatoryStep state={state} onSaved={load} />}
        {step === "insurance" && <InsuranceStep state={state} onSaved={load} />}
        {step === "conflict" && <ConflictStep state={state} onSaved={load} />}
        {step === "clauses" && <ClausesStep state={state} onSaved={load} />}
        {step === "sign" && <SignStep state={state} onSaved={load} />}
      </div>
    </div>
  );
}

function isStepDone(stepKey, c) {
  switch (stepKey) {
    case "company": return c.company_info_complete && c.bank_details_complete;
    case "signatory": return c.signatory_complete;
    case "insurance": return c.insurance_confirmed;
    case "conflict": return c.conflict_declared;
    case "clauses": return c.gdpr_accepted && c.non_bypass_accepted && c.deductions_accepted;
    case "sign": return c.agreement_signed;
    default: return false;
  }
}

function Field({ label, children, hint, required }) {
  return (
    <label className="block mb-3">
      <div className="text-xs uppercase tracking-widest text-brand-text2 mb-1">
        {label} {required && <span className="text-brand-red">*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-brand-text2 mt-1">{hint}</div>}
    </label>
  );
}

function StepCard({ title, children, onSubmit, busy, submitLabel = "Gem & fortsæt" }) {
  return (
    <form onSubmit={onSubmit} className="bg-white border border-brand p-6 space-y-3" data-testid="onboarding-step-card">
      <h2 className="font-display font-bold text-xl text-brand-dark mb-4">{title}</h2>
      {children}
      <div className="pt-4 border-t border-gray-100">
        <button type="submit" disabled={busy}
                className="px-5 py-3 bg-brand-red hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="onboarding-step-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ChevronRight size={14} />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function CompanyStep({ state, onSaved }) {
  const [busy, setBusy] = React.useState(false);
  const [f, setF] = React.useState({
    company_name: state.company_name || "",
    cvr: "",
    address: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    invoice_email: "",
    vat_registered: true,
    bank_reg: "",
    bank_account: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await scApi().post("/sc/onboarding/company-info", f);
      toast.success("Firmaoplysninger gemt");
      onSaved();
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setBusy(false); }
  };

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <StepCard title="1 · Firmaoplysninger & bankdetaljer" onSubmit={submit} busy={busy}>
      <Field label="Firmanavn" required>
        <input className="form-input w-full px-3 py-2 border border-brand" value={f.company_name}
               onChange={upd("company_name")} required data-testid="onboarding-company-name" />
      </Field>
      <Field label="CVR-nummer (8 cifre)" required>
        <input className="form-input w-full px-3 py-2 border border-brand" value={f.cvr}
               onChange={upd("cvr")} pattern="\d{8}" maxLength={8} required data-testid="onboarding-cvr" />
      </Field>
      <Field label="Adresse" required>
        <input className="form-input w-full px-3 py-2 border border-brand" value={f.address}
               onChange={upd("address")} required data-testid="onboarding-address" />
      </Field>
      <Field label="Kontaktperson" required>
        <input className="form-input w-full px-3 py-2 border border-brand" value={f.contact_name}
               onChange={upd("contact_name")} required data-testid="onboarding-contact-name" />
      </Field>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Email" required>
          <input type="email" className="form-input w-full px-3 py-2 border border-brand"
                 value={f.contact_email} onChange={upd("contact_email")} required data-testid="onboarding-contact-email" />
        </Field>
        <Field label="Telefon" required>
          <input className="form-input w-full px-3 py-2 border border-brand"
                 value={f.contact_phone} onChange={upd("contact_phone")} required data-testid="onboarding-contact-phone" />
        </Field>
      </div>
      <Field label="Faktura-email" required hint="EMA sender afregningsoplæg hertil">
        <input type="email" className="form-input w-full px-3 py-2 border border-brand"
               value={f.invoice_email} onChange={upd("invoice_email")} required data-testid="onboarding-invoice-email" />
      </Field>
      <Field label="Momsregistreret?" required>
        <select className="form-input w-full px-3 py-2 border border-brand"
                value={f.vat_registered ? "yes" : "no"}
                onChange={(e) => setF({ ...f, vat_registered: e.target.value === "yes" })}
                data-testid="onboarding-vat-registered">
          <option value="yes">Ja</option>
          <option value="no">Nej</option>
        </select>
      </Field>
      <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 mt-3">
        🔒 Bankoplysninger er adgangsbeskyttede. Kun du og EMA Kurer admin kan se det fulde kontonummer. Chauffører ser ALDRIG bankoplysninger.
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Reg.nr. (4 cifre)" required>
          <input className="form-input w-full px-3 py-2 border border-brand" value={f.bank_reg}
                 onChange={upd("bank_reg")} pattern="\d{4}" maxLength={4} required data-testid="onboarding-bank-reg" />
        </Field>
        <Field label="Kontonummer" required>
          <input className="form-input w-full px-3 py-2 border border-brand" value={f.bank_account}
                 onChange={upd("bank_account")} pattern="\d{4,20}" maxLength={20} required data-testid="onboarding-bank-account" />
        </Field>
      </div>
    </StepCard>
  );
}

function SignatoryStep({ state, onSaved }) {
  const [busy, setBusy] = React.useState(false);
  const [f, setF] = React.useState({
    full_name: "", role_title: "", email: "", phone: "", authorized_to_sign: false,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.authorized_to_sign) return toast.error("Du skal bekræfte at du har tegningsret");
    setBusy(true);
    try {
      await scApi().post("/sc/onboarding/signatory", f);
      toast.success("Underskriver gemt");
      onSaved();
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setBusy(false); }
  };

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <StepCard title="2 · Underskriver-information" onSubmit={submit} busy={busy}>
      <div className="text-xs text-brand-text2 mb-3">
        Den person der underskriver aftalen på vegne af firmaet skal udfylde nedenstående og bekræfte tegningsret.
      </div>
      <Field label="Fulde navn" required>
        <input className="form-input w-full px-3 py-2 border border-brand"
               value={f.full_name} onChange={upd("full_name")} required data-testid="onboarding-sig-name" />
      </Field>
      <Field label="Rolle / titel" required>
        <input className="form-input w-full px-3 py-2 border border-brand"
               value={f.role_title} onChange={upd("role_title")} required data-testid="onboarding-sig-role" />
      </Field>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Email" required>
          <input type="email" className="form-input w-full px-3 py-2 border border-brand"
                 value={f.email} onChange={upd("email")} required data-testid="onboarding-sig-email" />
        </Field>
        <Field label="Telefon" required>
          <input className="form-input w-full px-3 py-2 border border-brand"
                 value={f.phone} onChange={upd("phone")} required data-testid="onboarding-sig-phone" />
        </Field>
      </div>
      <label className="flex items-start gap-2 mt-4 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={f.authorized_to_sign}
               onChange={(e) => setF({ ...f, authorized_to_sign: e.target.checked })}
               data-testid="onboarding-sig-authorized" />
        <span className="text-sm">Jeg bekræfter at jeg er tegningsberettiget og kan binde firmaet juridisk.</span>
      </label>
    </StepCard>
  );
}

function InsuranceStep({ state, onSaved }) {
  const [busy, setBusy] = React.useState(false);
  const [f, setF] = React.useState({ vehicle_liability: false, work_injury: false, transport_liability: false });

  const submit = async (e) => {
    e.preventDefault();
    if (!(f.vehicle_liability && f.work_injury && f.transport_liability))
      return toast.error("Du skal bekræfte alle 3 forsikringer");
    setBusy(true);
    try {
      await scApi().post("/sc/onboarding/insurance", f);
      toast.success("Forsikringer bekræftet");
      onSaved();
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setBusy(false); }
  };

  const items = [
    ["vehicle_liability", "Gyldig køretøjs-ansvarsforsikring", "onboarding-ins-vehicle"],
    ["work_injury", "Arbejdsskadeforsikring for egne chauffører", "onboarding-ins-work"],
    ["transport_liability", "Erhvervs-/transportansvarsforsikring", "onboarding-ins-transport"],
  ];

  return (
    <StepCard title="3 · Forsikringsbekræftelse" onSubmit={submit} busy={busy}>
      <div className="bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 mb-4">
        EMA Kurer kan til enhver tid bede om dokumentation. Bekræftelser er bindende.
      </div>
      {items.map(([k, lbl, tid]) => (
        <label key={k} className="flex items-start gap-2 mb-3 cursor-pointer">
          <input type="checkbox" className="mt-1" checked={f[k]}
                 onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={tid} />
          <span className="text-sm">{lbl}</span>
        </label>
      ))}
    </StepCard>
  );
}

function ConflictStep({ state, onSaved }) {
  const [busy, setBusy] = React.useState(false);
  const [f, setF] = React.useState({
    declared: false, competitor_relationship: false,
    competitor_company_name: "", relationship_type: "subcontractor",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.declared) return toast.error("Du skal afgive erklæring om interessekonflikt");
    if (f.competitor_relationship && !f.competitor_company_name)
      return toast.error("Angiv konkurrent-firmanavn");
    setBusy(true);
    try {
      await scApi().post("/sc/onboarding/conflict", f);
      toast.success("Erklæring gemt");
      onSaved();
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setBusy(false); }
  };

  return (
    <StepCard title="4 · Interessekonflikt-erklæring" onSubmit={submit} busy={busy}>
      <div className="text-xs text-brand-text2 mb-3">
        Vognmænd må ikke bruge EMA Kurer kundedata, ruter, priser eller systemer til konkurrenter. Eventuelle relationer skal oplyses for review.
      </div>
      <label className="flex items-start gap-2 mb-3 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={f.declared}
               onChange={(e) => setF({ ...f, declared: e.target.checked })}
               data-testid="onboarding-conflict-declared" />
        <span className="text-sm">Jeg erklærer hermed status for relation til EMA Kurer-konkurrenter.</span>
      </label>
      <label className="flex items-start gap-2 mb-3 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={f.competitor_relationship}
               onChange={(e) => setF({ ...f, competitor_relationship: e.target.checked })}
               data-testid="onboarding-conflict-relation" />
        <span className="text-sm">Jeg, mine ejere eller mine tilknyttede chauffører har relation til en direkte konkurrent.</span>
      </label>
      {f.competitor_relationship && (
        <div className="ml-6 space-y-2 bg-amber-50 border border-amber-200 p-3">
          <Field label="Konkurrent-firmanavn" required>
            <input className="form-input w-full px-3 py-2 border border-brand"
                   value={f.competitor_company_name}
                   onChange={(e) => setF({ ...f, competitor_company_name: e.target.value })}
                   data-testid="onboarding-conflict-competitor" />
          </Field>
          <Field label="Relationstype" required>
            <select className="form-input w-full px-3 py-2 border border-brand"
                    value={f.relationship_type}
                    onChange={(e) => setF({ ...f, relationship_type: e.target.value })}
                    data-testid="onboarding-conflict-type">
              <option value="employee">Ansat</option>
              <option value="subcontractor">Underleverandør</option>
              <option value="owner">Ejer</option>
              <option value="partner">Partner</option>
              <option value="other">Andet</option>
            </select>
          </Field>
          <div className="text-xs text-amber-900">
            ⚠️ Dette markeres til admin-review. EMA Kurer kan godkende, afvise eller blokere baseret på konflikt-risiko.
          </div>
        </div>
      )}
    </StepCard>
  );
}

function ClausesStep({ state, onSaved }) {
  const [busy, setBusy] = React.useState(false);
  const [f, setF] = React.useState({
    gdpr_accepted: false, non_bypass_accepted: false, deductions_accepted: false,
    independent_contractor_accepted: false, driver_responsibility_accepted: false,
    ema_rules_accepted: false,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!Object.values(f).every(Boolean))
      return toast.error("Alle 6 klausuler skal accepteres");
    setBusy(true);
    try {
      await scApi().post("/sc/onboarding/accept-clauses", f);
      toast.success("Klausuler accepteret");
      onSaved();
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setBusy(false); }
  };

  const clauses = [
    ["independent_contractor_accepted", "Selvstændig vognmand", "Jeg er en uafhængig underleverandør — ikke en EMA Kurer-medarbejder.", "onboarding-clause-independent"],
    ["driver_responsibility_accepted", "Ansvar for egne chauffører", "Jeg er ansvarlig for mine egne chauffører, køretøjer, skat, løn, ferieløn, forsikring, arbejdsskade og ansættelsesforhold.", "onboarding-clause-driver-resp"],
    ["ema_rules_accepted", "EMA Kurer driftsregler", "Mine chauffører skal følge EMA Kurer reglerne: scanning, GPS/live tracking, POD/foto, kundeadfærd, deadlines, HUB/burafhentning, no-show regler, returproces, skadeproces og GDPR.", "onboarding-clause-ema-rules"],
    ["gdpr_accepted", "Fortrolighed & GDPR", "Jeg accepterer fuld fortrolighed og GDPR-regler omkring kundedata.", "onboarding-clause-gdpr"],
    ["non_bypass_accepted", "Kunde non-bypass", "Jeg må IKKE kontakte EMA Kurer kunder direkte for at omgå EMA, og jeg deler ikke interne EMA-oplysninger med konkurrenter.", "onboarding-clause-non-bypass"],
    ["deductions_accepted", "Modregning af dokumenterede beløb", "EMA Kurer kan modregne dokumenterede beløb i fremtidige afregninger: no-show fee, skader, mistede pakker, kundekompensation osv. Jeg kan bestride før endelig modregning.", "onboarding-clause-deductions"],
  ];

  return (
    <StepCard title="5 · Klausul-accept" onSubmit={submit} busy={busy}>
      <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 mb-4">
        <b>Vigtigt:</b> Alle afregninger, fakturaer og betalinger mellem EMA Kurer ApS og vognmanden sker udelukkende i danske kroner (DKK).
        Fakturaer i anden valuta accepteres ikke.
      </div>
      {clauses.map(([k, title, body, tid]) => (
        <label key={k} className="block mb-3 cursor-pointer border border-brand p-3 hover:bg-brand-bg2">
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={f[k]}
                   onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={tid} />
            <div>
              <div className="font-semibold text-brand-dark text-sm">{title}</div>
              <div className="text-xs text-brand-text2 mt-1">{body}</div>
            </div>
          </div>
        </label>
      ))}
    </StepCard>
  );
}

function SignStep({ state, onSaved }) {
  const [busy, setBusy] = React.useState(false);
  const [typedName, setTypedName] = React.useState("");
  const [confirm, setConfirm] = React.useState(false);
  const signatoryName = state.signatory?.full_name || "(udfyld underskriver-trin først)";

  const submit = async (e) => {
    e.preventDefault();
    if (typedName.trim().toLowerCase() !== signatoryName.trim().toLowerCase())
      return toast.error("Det indtastede navn skal matche underskrivers navn nøjagtigt");
    if (!confirm) return toast.error("Bekræft tegningsret før underskrift");
    setBusy(true);
    try {
      await scApi().post("/sc/onboarding/sign-agreement",
        { typed_full_name: typedName.trim(), confirm_authorization: true });
      toast.success("Aftale underskrevet — afventer EMA godkendelse");
      onSaved();
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setBusy(false); }
  };

  if (state.checks?.agreement_signed) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 p-6 text-center" data-testid="onboarding-already-signed">
        <CheckCircle2 size={32} className="text-emerald-600 mx-auto mb-2" />
        <div className="font-display font-bold text-lg text-emerald-900">Aftale underskrevet</div>
        <div className="text-sm text-emerald-800 mt-1">
          Version {state.agreement_version_signed} · afventer EMA Kurer godkendelse.
        </div>
      </div>
    );
  }

  return (
    <StepCard title="6 · Underskriv Underleverandøraftale" onSubmit={submit} busy={busy} submitLabel="Underskriv og indsend">
      <div className="bg-white border border-brand p-4 text-sm text-brand-dark space-y-3 mb-4 max-h-72 overflow-y-auto">
        <div className="font-display font-bold">Underleverandøraftale — version {state.agreement_version_required}</div>
        <p>Mellem <b>EMA Kurer ApS</b> og <b>{state.company_name}</b> (CVR {state.cvr || "—"})</p>
        <p><b>§1 Selvstændighed:</b> Vognmanden er en selvstændig underleverandør og IKKE ansat hos EMA Kurer ApS.</p>
        <p><b>§2 Ansvar:</b> Vognmanden er ansvarlig for egne chauffører, køretøjer, skat, løn, ferieløn, forsikring, arbejdsskade og ansættelse.</p>
        <p><b>§3 Forsikringer:</b> Vognmanden bekræfter at have gyldig køretøjs-, arbejdsskade- og transportansvarsforsikring.</p>
        <p><b>§4 EMA regler:</b> Vognmanden og dennes chauffører skal følge EMA Kurer reglerne (scanning, GPS, POD, kundeadfærd, deadlines, HUB/bure, no-show, retur, skader, GDPR).</p>
        <p><b>§5 Valuta og afregning:</b> Alle afregning, fakturering og betaling mellem EMA Kurer ApS og vognmanden sker udelukkende i danske kroner (DKK). Fakturaer i anden valuta accepteres ikke. Betalingsfrist: netto 14 dage fra korrekt faktura.</p>
        <p><b>§5b Betalingsoplysninger:</b> Vognmanden er ansvarlig for, at oplyste betalingsoplysninger er korrekte og tilhører vognmandens virksomhed. EMA Kurer ApS foretager kun betaling i danske kroner (DKK) via godkendt dansk reg.nr./kontonummer eller FI-kode tilknyttet vognmandens selskab. Ændringer i betalingsoplysninger skal godkendes af EMA Kurer, før de anvendes til betaling.</p>
        <p><b>§6 Modregning:</b> EMA Kurer kan modregne dokumenterede beløb i fremtidige afregninger.</p>
        <p><b>§7 Konkurrence:</b> Vognmanden må ikke bruge EMA Kurer kundedata, priser, ruter eller systemer til konkurrenter, og må ikke kontakte EMA Kurer kunder direkte.</p>
        <p><b>§8 Tilsyn:</b> EMA Kurer kan godkende/afvise/blokere vognmænd, chauffører og køretøjer.</p>
      </div>
      <div className="bg-brand-bg2 border border-brand p-3 text-xs space-y-1 mb-4">
        <div><b>Underskriver:</b> {signatoryName}</div>
        <div><b>Rolle:</b> {state.signatory?.role_title || "—"}</div>
        <div><b>Email:</b> {state.signatory?.email || "—"}</div>
        <div><b>Firma:</b> {state.company_name} · CVR {state.cvr || "—"}</div>
      </div>
      <Field label={`Skriv dit fulde navn (skal matche "${signatoryName}")`} required>
        <input className="form-input w-full px-3 py-3 border border-brand text-lg font-semibold tracking-wider"
               style={{ fontFamily: "cursive" }}
               value={typedName}
               onChange={(e) => setTypedName(e.target.value)}
               required data-testid="onboarding-sign-typed-name" />
      </Field>
      <label className="flex items-start gap-2 mb-3 cursor-pointer">
        <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)}
               className="mt-1" data-testid="onboarding-sign-confirm" />
        <span className="text-sm">Jeg bekræfter min tegningsret og underskriver hermed elektronisk Underleverandøraftalen ovenfor.</span>
      </label>
      <div className="text-[11px] text-brand-text2">
        Vi registrerer din IP-adresse, browser, tidsstempel og din indtastede underskrift som bevis. En PDF-kvittering gemmes til revision.
      </div>
    </StepCard>
  );
}
